import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, FileText, XCircle } from 'lucide-react';

export const OrdersPage: React.FC = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        import('../lib/api/billing').then(({ billingApi }) => {
            billingApi.getAllOrders()
                .then(data => {
                    setOrders(data.map(order => ({
                        id: order.id,
                        orderNumber: order.orderNumber,
                        user: order.userName || order.userEmail || 'Unknown User',
                        email: order.userEmail || '—',
                        amount: (order.amount / 100).toLocaleString('en-US', { style: 'currency', currency: order.currency }),
                        status: order.status.toLowerCase(), // 'paid', 'pending', 'failed'
                        date: new Date(order.date).toLocaleDateString(),
                        rawStatus: order.status
                    })));
                })
                .catch(console.error)
                .finally(() => setLoading(false));
        });
    }, []);

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">

                {/* Toolbar */}
                <div className="flex items-center justify-between p-2 border-b border-neutral-200 bg-neutral-50/50">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-2">Orders</span>
                    <div className="flex items-center gap-2">
                        <select className="text-xs border border-neutral-200 rounded-md bg-white text-neutral-600 py-1 pl-2 pr-6 focus:ring-0 focus:border-neutral-300">
                            <option>All Orders</option>
                            <option>Succeeded</option>
                            <option>Pending</option>
                            <option>Refunded</option>
                        </select>
                    </div>
                </div>

                {/* Header Grid */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-3">Order #</div>
                    <div className="col-span-4">Type</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1 text-right">Date</div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <p className="text-sm">Loading orders...</p>
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <FileText size={32} className="mb-3 text-neutral-300" />
                            <p className="text-sm">No orders found</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {orders.map(order => (
                                <div key={order.id} onClick={() => navigate(`/payments/orders/${order.id}`)} className="grid grid-cols-12 gap-4 px-4 py-2 items-center hover:bg-neutral-50/80 transition-colors text-sm group cursor-pointer">
                                    <div className="col-span-3 min-w-0 font-medium text-neutral-900">
                                        #{order.orderNumber}
                                    </div>
                                    <div className="col-span-4 min-w-0">
                                        <div className="text-neutral-500 truncate text-xs">Subscription Payment</div>
                                    </div>
                                    <div className="col-span-2 font-medium text-neutral-900">
                                        {order.amount}
                                    </div>
                                    <div className="col-span-2">
                                        {order.rawStatus === 'PAID' && <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle size={10} /> Paid</span>}
                                        {order.rawStatus === 'PENDING' && <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium"><Clock size={10} /> Pending</span>}
                                        {order.rawStatus === 'FAILED' && <span className="inline-flex items-center gap-1 text-xs text-red-600 font-medium"><XCircle size={10} /> Failed</span>}
                                    </div>
                                    <div className="col-span-1 text-right text-xs text-neutral-500">
                                        {order.date}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// FailedPaymentsPage removed

// PlansPage moved to ./payment_pages/PlansPage.tsx
